import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import { SidebarNavItemProps } from '../../props';
import { StyledListItemButton } from './styled';

const MenuIcon = ({
  item,
  isSelected,
  isSideBarOpen,
  level,
}: {
  item: SidebarNavItemProps['item'];
  isSelected: boolean;
  isSideBarOpen: boolean;
  level: number;
}) => {
  const iconSelectedColor = 'white';

  if (item.iconSelected && isSelected) {
    const IconSelected = item.iconSelected;
    return <IconSelected strokeWidth={1.5} size={isSideBarOpen ? '10px' : '24px'} sx={{ color: iconSelectedColor }} />;
  }

  if (item.icon) {
    const Icon = item.icon;
    return <Icon strokeWidth={1.5} size={isSideBarOpen ? '10px' : '24px'} sx={{ color: iconSelectedColor }} />;
  }

  return (
    <FiberManualRecordIcon
      sx={{
        width: isSelected ? 8 : 6,
        height: isSelected ? 8 : 6,
        color: iconSelectedColor,
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );
};

const SidebarNavItem = ({ item, level, isSideBarOpen }: SidebarNavItemProps) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const isSelected = (item.url || item.link) === pathname + search;
  const iconSelectedColor = 'white';

  const handleItemClick = () => {
    if (!item.disabled) {
      navigate(item.url!);
    }
  };

  return (
    <Tooltip title={!isSideBarOpen ? item.title : ''} placement="right" arrow disableHoverListener={isSideBarOpen}>
      <StyledListItemButton
        level={level}
        isSideBarOpen={isSideBarOpen}
        iconSelectedColor={iconSelectedColor}
        disabled={item.disabled}
        disableRipple={!isSideBarOpen}
        selected={isSelected}
        onClick={handleItemClick}
      >
        <ListItemIcon
          sx={{
            border: isSelected && !isSideBarOpen ? '1px solid white' : 'none',
            minWidth: 0,
            mr: 1,
          }}
        >
          <MenuIcon item={item} isSelected={isSelected} isSideBarOpen={isSideBarOpen} level={level} />
        </ListItemIcon>

        {(isSideBarOpen || (!isSideBarOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                noWrap
                overflow="hidden"
                textOverflow="ellipsis"
                variant="body1"
                sx={{ color: iconSelectedColor }}
                width="120px"
                fontWeight={isSelected ? 'bold' : 'normal'}
              >
                {typeof item.title === 'string' ? item.title.toUpperCase() : item.title}
              </Typography>
            }
            sx={{ flex: 'none' }}
            secondary={
              item.caption && (
                <Typography variant="caption" sx={{ color: iconSelectedColor }} display="block" gutterBottom>
                  {item.caption}
                </Typography>
              )
            }
          />
        )}

        {isSideBarOpen && item.chip && (
          <Box sx={{ ml: 'auto' }}>
            <Chip {...item.chip} />
          </Box>
        )}
      </StyledListItemButton>
    </Tooltip>
  );
};

export default SidebarNavItem;
